import { useState, useEffect, useRef, useCallback } from "react";
import "../../../styles/proMajazi/user/ProMajaziDashboard.css"; // کارت‌ها (pmd-card)
import "../../../styles/proMajazi/user/ProMajaziTokens.css";
import { swalError, swalSuccess } from "../../../utils/swalAlert";

import { buyToken, dailyLimit, getUsdtPrice } from "../../../api/proMajazi";

// بازه‌ی انتخاب با اسلایدر (قیمت هر توکن از والد و از تنظیمات می‌آید)
const MIN_TOKENS = 1;
const MAX_TOKENS = 1000;

// اطلاعات کارت مقصد
const CARD_INFO = {
  number: "6104-3388-0126-4298",
  owner: " گروه مهندسی باورسازان هوشمند",
  bank: "بانک ملی",
};

const METHODS = [
  { id: "gateway", label: "درگاه پرداخت", sub: "پرداخت آنلاین با کارت بانکی" },
  { id: "card", label: "کارت به کارت", sub: "واریز دستی و ثبت شناسه واریز" },
];

const QUICK_COUNTS = [10, 50, 100, 500, 1000];

const fa = (n) => Number(n).toLocaleString("fa-IR");

// استخراج قیمت دلار (تومان) از پاسخ API: فیلد price_toman
const extractRate = (data) => {
  console.log(data);
  const n = Number(data?.day_high_irr) * 0.1;
  return Number.isFinite(n) && n > 0 ? n : null;
};

// سلکت باکس سفارشی
export function CustomSelect({ id, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const ref = useRef(null);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) =>
      ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openList = () => {
    setHi(selectedIndex);
    setOpen(true);
  };
  const choose = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (options[hi]) choose(options[hi]);
    }
  };

  return (
    <div className={`pmt-select${open ? " open" : ""}`} ref={ref}>
      <button
        id={id}
        type="button"
        className="pmt-select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      >
        <span>{selected?.label}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="pmt-select-list" role="listbox">
          {options.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              className={`pmt-select-opt${o.value === value ? " selected" : ""}${i === hi ? " hi" : ""}`}
              onMouseEnter={() => setHi(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o)}
            >
              <span>{o.label}</span>
              {o.value === value && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProMajaziTokens({
  shop,
  tokenPrice,
  priceError,
  onDailyLimitChange,
  onPurchaseCreated,
}) {
  const [tokenCount, setTokenCount] = useState(10);
  const [countText, setCountText] = useState("10");

  const [usdRate, setUsdRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError] = useState(false);
  const aliveRef = useRef(true);

  const loadRate = useCallback(async (manual = false) => {
    setRateLoading(true);
    try {
      const res = await getUsdtPrice();
      const rate = extractRate(res.data);
      if (!aliveRef.current) return;
      if (rate == null) throw new Error("invalid usdt price response");
      setUsdRate(rate);
      setRateError(false);
    } catch (err) {
      if (!aliveRef.current) return;
      console.error(err);
      setUsdRate((prev) => {
        if (prev == null) setRateError(true);
        return prev;
      });
      if (manual) {
        swalError({
          title: "خطا در دریافت قیمت دلار",
          text: err.response?.data?.detail || "دوباره تلاش کنید.",
        });
      }
    } finally {
      if (aliveRef.current) setRateLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    loadRate();
    return () => {
      aliveRef.current = false;
    };
  }, [loadRate]);

  // ==========================================
  // محاسبات قیمت پایه، مالیات ۱۰٪ و قیمت نهایی
  // ==========================================
  const tomanPerToken =
    tokenPrice != null && usdRate != null ? tokenPrice * usdRate : null;
  const priceReady = tomanPerToken != null;

  // ۱. مبلغ پایه توکن‌ها (تومان)
  const basePrice = priceReady ? Math.round(tokenCount * tomanPerToken) : 0;
  // ۲. ارزش افزوده ۱۰٪
  const taxAmount = priceReady ? Math.round(basePrice * 0.1) : 0;
  // ۳. مبلغ نهایی قابل پرداخت (پایه + ۱۰٪ مالیات) که ارسال خواهد شد
  const totalPrice = basePrice + taxAmount;

  const anyPriceError = priceError || rateError;
  const sliderPct =
    ((tokenCount - MIN_TOKENS) / (MAX_TOKENS - MIN_TOKENS)) * 100;

  // محدودیت روزانه
  const [unlimited, setUnlimited] = useState(shop.dailyLimit == null);
  const [limit, setLimit] = useState(shop.dailyLimit ?? "");
  const [limitMsg, setLimitMsg] = useState(null);
  const [savingLimit, setSavingLimit] = useState(false);

  const clampCount = (v) => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n)) return MIN_TOKENS;
    return Math.min(MAX_TOKENS, Math.max(MIN_TOKENS, n));
  };

  const handleCountInput = (v) => {
    setCountText(v);
    if (v === "") return;
    const n = Number(v);
    if (Number.isFinite(n) && n >= MIN_TOKENS && n <= MAX_TOKENS) {
      setTokenCount(Math.round(n));
    }
  };
  const commitCount = () => {
    const n = clampCount(countText);
    setTokenCount(n);
    setCountText(String(n));
    return n;
  };
  const handleSlider = (v) => {
    const n = clampCount(v);
    setTokenCount(n);
    setCountText(String(n));
  };

  // مدال پرداخت
  const [modalOpen, setModalOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("gateway");
  const [depositId, setDepositId] = useState("");
  const [modalErr, setModalErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const openModal = () => {
    if (!priceReady) return;
    commitCount();
    setPayMethod("gateway");
    setDepositId("");
    setModalErr(null);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => e.key === "Escape" && setModalOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const copyCard = async () => {
    try {
      await navigator.clipboard.writeText(CARD_INFO.number.replace(/-/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* دستی کپی می‌شود */
    }
  };

  const handleConfirmPay = async () => {
    if (submitting) return;

    const id = depositId.trim();
    if (payMethod === "card" && id.length < 4) {
      setModalErr("شناسه واریز را وارد کنید");
      return;
    }

    if (!priceReady || !(totalPrice > 0)) {
      setModalErr("قیمت هنوز دریافت نشده است. دوباره تلاش کنید.");
      return;
    }

    setSubmitting(true);
    setModalErr(null);
    let redirecting = false;
    try {
      // ارسال مبلغ نهایی شامل ۱۰٪ مالیات به بک‌اند
      const res = await buyToken({
        shop_id: shop.store_id,
        token_count: tokenCount,
        total_price: totalPrice, // مبلغ پایه + ۱۰٪ مالیات
        method: payMethod,
        deposit_id: payMethod === "card" ? id : undefined,
      });

      onPurchaseCreated?.();

      if (payMethod === "gateway") {
        const url = res.data?.payment_url;
        if (!url) throw new Error("URL درگاه دریافت نشد");
        redirecting = true;
        window.location.href = url;
        return;
      }

      closeModal();
      swalSuccess({
        title: "درخواست ثبت شد",
        text: "پس از تأیید واریز، توکن به حساب فروشگاه اضافه می‌شود.",
      });
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      swalError({
        title: "خطا در ثبت درخواست",
        text:
          data?.detail ||
          data?.message ||
          err.message ||
          "ثبت درخواست با خطا مواجه شد. دوباره تلاش کنید.",
      });
    } finally {
      if (!redirecting) setSubmitting(false);
    }
  };

  const handleSaveLimit = async () => {
    if (savingLimit) return;
    let value = null;
    if (!unlimited) {
      value = Number(limit);
      if (!Number.isInteger(value) || value <= 0) {
        setLimitMsg({
          type: "err",
          text: "یک عدد صحیح بزرگ‌تر از صفر وارد کنید",
        });
        return;
      }
    }
    setSavingLimit(true);
    setLimitMsg(null);
    try {
      await dailyLimit(shop.store_id, {
        unlimited,
        daily_limit: value,
      });
      onDailyLimitChange(value);
      swalSuccess({ text: "محدودیت روزانه ذخیره شد", title: "ذخیره شد" });
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      swalError({
        title: "خطا در ذخیره",
        text:
          data?.detail ||
          data?.message ||
          "ذخیره محدودیت با خطا مواجه شد. دوباره تلاش کنید.",
      });
    } finally {
      setSavingLimit(false);
    }
  };

  return (
    <div className="pmt pmt-embedded">
      {/* کارت خرید توکن */}
      <div className="pmd-card pmt-buy">
        <div className="pmt-buy-head">
          <h2>
            خرید توکن <span>«{shop.store_name}»</span>
          </h2>
          <button
            type="button"
            className={`pmt-rate-chip${rateLoading ? " loading" : ""}`}
            onClick={() => loadRate(true)}
            disabled={rateLoading}
            title="به‌روزرسانی قیمت دلار"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 11a8 8 0 1 0-2.3 5.7" />
              <path d="M20 4v7h-7" />
            </svg>
            {usdRate != null ? (
              <span>
                دلار: <b>{fa(usdRate)}</b> تومان
              </span>
            ) : rateLoading ? (
              <span>در حال دریافت...</span>
            ) : (
              <span className="pmt-rate-err">دریافت ناموفق بود</span>
            )}
          </button>
        </div>

        <div className="pmt-counter">
          <button
            type="button"
            className="pmt-step"
            aria-label="کم کردن"
            onClick={() => handleSlider(tokenCount - 1)}
            disabled={tokenCount <= MIN_TOKENS}
          >
            −
          </button>
          <div className="pmt-counter-mid">
            <input
              id="pmt-count-input"
              className="pmt-count-big"
              type="number"
              min={MIN_TOKENS}
              max={MAX_TOKENS}
              aria-label="تعداد توکن"
              value={countText}
              onChange={(e) => handleCountInput(e.target.value)}
              onBlur={commitCount}
            />
            <span>توکن</span>
          </div>
          <button
            type="button"
            className="pmt-step"
            aria-label="زیاد کردن"
            onClick={() => handleSlider(tokenCount + 1)}
            disabled={tokenCount >= MAX_TOKENS}
          >
            +
          </button>
        </div>

        <div className="pmt-slider-box">
          <input
            id="pmt-count"
            className="pmt-slider"
            type="range"
            min={MIN_TOKENS}
            max={MAX_TOKENS}
            step="1"
            aria-label="تعداد توکن"
            value={tokenCount}
            style={{ "--pct": `${sliderPct}%` }}
            onChange={(e) => handleSlider(e.target.value)}
          />
          <div className="pmt-slider-scale">
            <span>{fa(MIN_TOKENS)}</span>
            <span>{fa(MAX_TOKENS)}</span>
          </div>
        </div>

        <div className="pmt-quick">
          {QUICK_COUNTS.map((c) => (
            <button
              key={c}
              type="button"
              className={`pmt-chip${tokenCount === c ? " active" : ""}`}
              onClick={() => handleSlider(c)}
            >
              {fa(c)}
            </button>
          ))}
        </div>

        {/* بخش نمایش قیمت با تفکیک ۱۰ درصد مالیات */}
        <div className="pmt-pay">
          <div className="pmt-pay-info">
            {priceReady ? (
              <>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    marginBottom: "4px",
                  }}
                >
                  مبلغ توکن‌ها: {fa(basePrice)} تومان + مالیات (۱۰٪):{" "}
                  {fa(taxAmount)} تومان
                </div>
                <span>مبلغ نهایی قابل پرداخت:</span>
                <strong>
                  {fa(totalPrice)} <small>تومان</small>
                </strong>
                {tomanPerToken != null && (
                  <em>هر توکن خام ≈ {fa(Math.round(tomanPerToken))} تومان</em>
                )}
              </>
            ) : (
              <strong className="pmt-pay-wait">
                {anyPriceError
                  ? "دریافت قیمت ناموفق بود"
                  : "در حال دریافت قیمت..."}
              </strong>
            )}
          </div>
          <button
            type="button"
            className="pmt-btn pmt-btn-pay"
            onClick={openModal}
            disabled={!priceReady || rateLoading}
          >
            پرداخت
          </button>
        </div>
      </div>

      {/* محدودیت روزانه */}
      <div className="pmd-card">
        <h2>محدودیت توکن روزانه‌ی مشتریان</h2>
        <label className="pmt-check">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={(e) => {
              setUnlimited(e.target.checked);
              setLimitMsg(null);
            }}
          />
          بدون محدودیت
        </label>
        <div className="pmt-field">
          <label htmlFor="pmt-limit">حداکثر توکن مصرفی هر مشتری در روز</label>
          <input
            id="pmt-limit"
            className="pmt-input"
            type="number"
            min="1"
            inputMode="numeric"
            placeholder="مثلاً ۲۰۰۰"
            value={unlimited ? "" : limit}
            disabled={unlimited}
            onChange={(e) => setLimit(e.target.value)}
          />
        </div>
        <div className="pmt-hint">
          هر مشتری فروشگاه بیشتر از این مقدار در روز نمی‌تواند توکن مصرف کند.
          سقف هر روز از نیمه‌شب دوباره صفر می‌شود.
        </div>
        <div className="pmt-row">
          <span />
          <button
            type="button"
            className="pmt-btn"
            onClick={handleSaveLimit}
            disabled={savingLimit}
          >
            {savingLimit ? "در حال ذخیره..." : "ذخیره محدودیت"}
          </button>
        </div>
        {limitMsg && (
          <div className={`pmt-msg ${limitMsg.type}`}>{limitMsg.text}</div>
        )}
      </div>

      {/* مدال پرداخت */}
      {modalOpen && (
        <div
          className="pmt-overlay"
          onMouseDown={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div
            className="pmt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pmt-modal-title"
          >
            <div className="pmt-modal-head">
              <h2 id="pmt-modal-title">روش پرداخت</h2>
              <button
                type="button"
                className="pmt-modal-close"
                aria-label="بستن"
                onClick={closeModal}
              >
                ✕
              </button>
            </div>

            {/* تفکیک فاکتور در مدال */}
            <div className="pmt-summary">
              <div>
                <span>فروشگاه</span>
                <b>{shop.store_name}</b>
              </div>
              <div>
                <span>تعداد توکن</span>
                <b>{fa(tokenCount)} توکن</b>
              </div>
              <div>
                <span>مبلغ توکن‌ها</span>
                <span>{fa(basePrice)} تومان</span>
              </div>
              <div>
                <span>مالیات ارزش افزوده (۱۰٪)</span>
                <span>{fa(taxAmount)} تومان</span>
              </div>
              <div
                style={{
                  borderTop: "1px dashed #ddd",
                  paddingTop: 8,
                  marginTop: 4,
                }}
              >
                <span style={{ fontWeight: "bold" }}>مبلغ نهایی واریزی</span>
                <b
                  style={{
                    color: "var(--primary-color, #2563eb)",
                    fontSize: "1.1rem",
                  }}
                >
                  {fa(totalPrice)} تومان
                </b>
              </div>
            </div>

            <div
              className="pmt-methods"
              role="radiogroup"
              aria-label="روش پرداخت"
            >
              {METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={payMethod === m.id}
                  className={`pmt-package${payMethod === m.id ? " selected" : ""}`}
                  onClick={() => {
                    setPayMethod(m.id);
                    setModalErr(null);
                  }}
                >
                  <strong>{m.label}</strong>
                  <span>{m.sub}</span>
                </button>
              ))}
            </div>

            {payMethod === "card" && (
              <div className="pmt-cardbox">
                <div className="pmt-cardline">
                  <span>شماره کارت</span>
                  <b className="pmt-cardnum">{CARD_INFO.number}</b>
                  <button type="button" className="pmt-copy" onClick={copyCard}>
                    {copied ? "کپی شد" : "کپی"}
                  </button>
                </div>
                <div className="pmt-cardline">
                  <span>به نام</span>
                  <b>{CARD_INFO.owner}</b>
                  <span className="pmt-bank">{CARD_INFO.bank}</span>
                </div>
                <div
                  className="pmt-cardline"
                  style={{ marginTop: 6, color: "#d97706" }}
                >
                  <span>مبلغ دقیق جهت واریز:</span>
                  <b>{fa(totalPrice)} تومان</b>
                </div>
                <div className="pmt-field" style={{ marginTop: 12 }}>
                  <label htmlFor="pmt-deposit">شناسه واریز</label>
                  <input
                    id="pmt-deposit"
                    className="pmt-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="شناسه واریز / شماره پیگیری تراکنش"
                    value={depositId}
                    onChange={(e) => setDepositId(e.target.value)}
                  />
                </div>
                <div className="pmt-hint">
                  مبلغ دقیق ({fa(totalPrice)} تومان) را به کارت بالا واریز کنید
                  و شناسه واریز را وارد نمایید.
                </div>
              </div>
            )}

            {modalErr && <div className="pmt-msg err">{modalErr}</div>}

            <div className="pmt-modal-actions">
              <button
                type="button"
                className="pmt-btn-ghost"
                onClick={closeModal}
              >
                انصراف
              </button>
              <button
                type="button"
                className="pmt-btn"
                onClick={handleConfirmPay}
                disabled={submitting}
              >
                {submitting
                  ? "در حال ارسال..."
                  : payMethod === "card"
                    ? "ثبت واریز"
                    : `پرداخت آنلاین (${fa(totalPrice)} تومان)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
